<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from "vue";

type TurnstileApi = {
  render(
    container: HTMLElement,
    options: {
      sitekey: string;
      action: string;
      size: "flexible";
      callback: (token: string) => void;
      "expired-callback": () => void;
      "error-callback": () => void;
    },
  ): string;
  reset(widgetId: string): void;
  remove(widgetId: string): void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const props = defineProps<{
  sitekey: string;
  action: string;
}>();
const emit = defineEmits<{
  (event: "update:token", token: string): void;
  (event: "error"): void;
}>();

const container = ref<HTMLElement | null>(null);
let widgetId: string | null = null;
let scriptPromise: Promise<TurnstileApi> | null = null;

function loadTurnstile() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<TurnstileApi>((resolve, reject) => {
    const script = document.createElement("script");
    script.src =
      "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.onload = () =>
      window.turnstile
        ? resolve(window.turnstile)
        : reject(new Error('奴才该死！人机验证没能加载出来，还请吾皇再试一次！😭'));
    script.onerror = () => reject(new Error('奴才该死！人机验证没能加载出来，还请吾皇再试一次！😭'));
    document.head.appendChild(script);
  }).catch((error) => {
    scriptPromise = null;
    throw error;
  });
  return scriptPromise;
}

function clearToken() {
  emit("update:token", "");
}

function reset() {
  clearToken();
  if (widgetId && window.turnstile) window.turnstile.reset(widgetId);
}

defineExpose({ reset });

onMounted(async () => {
  try {
    const turnstile = await loadTurnstile();
    if (!container.value) return;
    widgetId = turnstile.render(container.value, {
      sitekey: props.sitekey,
      action: props.action,
      size: "flexible",
      callback: (token) => emit("update:token", token),
      "expired-callback": clearToken,
      "error-callback": () => {
        clearToken();
        emit("error");
      },
    });
  } catch {
    emit("error");
  }
});

onBeforeUnmount(() => {
  if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
});
</script>

<template>
  <div
    ref="container"
    class="cf-turnstile"
    :data-sitekey="sitekey"
    data-action="turnstile-spin-v2"
    data-size="flexible"
  ></div>
</template>

<style scoped>
.cf-turnstile {
  width: 100%;
}
</style>
